import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export function ExtractionStatus({ extractionStatus }) {
  const { metrics, batch_progress, is_running, progress } = extractionStatus;

  if (!is_running) {
    return (
      <Card className="bg-slate-900/50 border-slate-800" data-testid="status-panel">
        <CardHeader><CardTitle>Status</CardTitle></CardHeader>
        <CardContent>
          <div className="text-slate-500 text-sm">Select subdivisions and confirm to start extraction.</div>
        </CardContent>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card className="bg-slate-900/50 border-slate-800" data-testid="status-panel">
        <CardHeader><CardTitle>Extraction Progress</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-cyan-500/50 rounded-full animate-pulse w-1/3" />
            </div>
            <ScrollArea className="h-32 bg-slate-800/50 rounded-lg p-3">
              <div className="space-y-1 font-mono text-xs">
                {progress?.map((log, i) => <div key={`log-${i}`} className="text-slate-400">{log}</div>)}
              </div>
            </ScrollArea>
          </div>
        </CardContent>
      </Card>
    );
  }

  const memRatio = metrics.memory_limit_mb > 0
    ? metrics.memory_mb / metrics.memory_limit_mb
    : 0;

  const memColor = memRatio > 0.8 ? "bg-red-500" : memRatio > 0.6 ? "bg-amber-500" : "bg-emerald-500";

  const phases = ["downloading", "pass1", "pass2", "dedup", "done"];
  const phaseLabels = { downloading: "Download", pass1: "Nodes", pass2: "Ways", dedup: "Dedup", done: "Done" };
  const currentPhaseIdx = phases.indexOf(metrics.phase);

  return (
    <Card className="bg-slate-900/50 border-slate-800" data-testid="status-panel">
      <CardHeader><CardTitle>Extraction Progress</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Batch indicator */}
          {batch_progress && (
            <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2">
              <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">
                {batch_progress.current}/{batch_progress.total}
              </Badge>
              <span className="text-sm text-cyan-300">Processing: {batch_progress.current_name}</span>
            </div>
          )}

          {/* Phase indicator */}
          <div className="flex items-center gap-1.5">
            {phases.map((p, i) => {
              const isActive = p === metrics.phase;
              const isDone = i < currentPhaseIdx || metrics.phase === "done";
              return (
                <div key={p} className="flex items-center gap-1.5 flex-1">
                  <div
                    data-testid={`phase-${p}`}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${
                      isDone ? "bg-cyan-500" : isActive ? "bg-cyan-500 animate-pulse" : "bg-slate-700"
                    }`}
                  />
                  <span className={`text-[10px] whitespace-nowrap ${
                    isActive ? "text-cyan-400 font-medium" : isDone ? "text-slate-400" : "text-slate-600"
                  }`}>{phaseLabels[p]}</span>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-400">{metrics.message}</span>
              <span data-testid="progress-pct" className="text-cyan-400 font-mono">{metrics.progress_pct}%</span>
            </div>
            <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                data-testid="progress-bar"
                className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 rounded-full transition-all duration-700 ease-out"
                style={{ width: `${metrics.progress_pct}%` }}
              />
            </div>
          </div>

          {/* Live stats */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="bg-slate-800/70 rounded-lg p-2">
              <div className="text-slate-500 mb-0.5">Memory</div>
              <div className="flex items-center gap-1">
                <div className="flex-1 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    data-testid="memory-bar"
                    className={`h-full rounded-full transition-all duration-500 ${memColor}`}
                    style={{ width: `${Math.min(100, memRatio * 100)}%` }}
                  />
                </div>
                <span data-testid="memory-value" className="font-mono text-slate-300 whitespace-nowrap">{metrics.memory_mb} MB</span>
              </div>
              {metrics.memory_limit_mb > 0 && (
                <div className="text-slate-600 text-[10px] mt-0.5">/ {metrics.memory_limit_mb} MB limit</div>
              )}
            </div>
            <div className="bg-slate-800/70 rounded-lg p-2">
              <div className="text-slate-500 mb-0.5">Beaches</div>
              <div data-testid="beaches-found-count" className="text-lg font-mono text-cyan-400">{metrics.beaches_named}</div>
              <div className="text-slate-600 text-[10px]">{metrics.beaches_found} total scanned</div>
            </div>
            <div className="bg-slate-800/70 rounded-lg p-2">
              <div className="text-slate-500 mb-0.5">PBF File</div>
              <div className="text-lg font-mono text-slate-300">{metrics.file_size_mb} <span className="text-xs text-slate-500">MB</span></div>
              {metrics.dups_removed > 0 && (
                <div className="text-slate-600 text-[10px]">{metrics.dups_removed} dupes removed</div>
              )}
            </div>
          </div>

          {/* Log tail */}
          <ScrollArea className="h-24 bg-slate-800/30 rounded-lg p-2">
            <div className="space-y-0.5 font-mono text-[10px]">
              {progress?.slice(-10).map((l, i) => <div key={`tail-${i}`} className="text-slate-500">{l}</div>)}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
    </Card>
  );
}
